import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Radix Select rather than a native <select>: the native element cannot show a description
 * under an option, and every role picker in this console needs one — "Supervisor" means
 * nothing without "one domain, reads but does not manage".
 */
export const Select = SelectPrimitive.Root;
export const SelectValue = SelectPrimitive.Value;
export const SelectGroup = SelectPrimitive.Group;

export function SelectTrigger({
  className, children, invalid, ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & { invalid?: boolean }) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        "flex h-8 w-full items-center justify-between gap-2 rounded-md border bg-surface",
        "px-2.5 text-base text-text shadow-xs transition-[border-color,box-shadow]",
        "data-[placeholder]:text-text-subtle",
        "focus:border-accent-8 focus:outline-none focus:ring-2 focus:ring-accent-8/25",
        "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-muted",
        "[&>span]:truncate",
        invalid ? "border-danger-9" : "border-border",
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-3.5 shrink-0 text-text-subtle" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectContent({
  className, children, position = "popper", ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        className={cn(
          "relative z-50 max-h-80 min-w-[8rem] overflow-hidden rounded-lg border border-border",
          "bg-surface-raised shadow-lg",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
          // As wide as the longest option needs, never narrower than the trigger, and never wider
          // than the trigger or 20rem (whichever is wider) or the room on screen: an option's
          // description wraps rather than stretching the list out of a sheet or off the page.
          position === "popper" && [
            "w-max min-w-[var(--radix-select-trigger-width)]",
            "max-w-[min(max(var(--radix-select-trigger-width),20rem),var(--radix-select-content-available-width))]",
          ],
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "p-1 scrollbar-subtle",
            // Match the trigger's width so a long option list does not reflow the layout.
            position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

export function SelectItem({
  className, children, description, ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & { description?: string }) {
  return (
    <SelectPrimitive.Item
      className={cn(
        "relative flex cursor-default select-none items-start gap-2 rounded px-2 py-1.5",
        "text-sm text-text outline-none",
        "data-[highlighted]:bg-surface-hover",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <span className="flex w-3.5 shrink-0 items-center justify-center pt-0.5">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-3.5 text-accent-11" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <span className="min-w-0 flex-1">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        {description && (
          <span className="mt-0.5 block text-xs leading-snug text-text-muted">{description}</span>
        )}
      </span>
    </SelectPrimitive.Item>
  );
}

export function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      className={cn("px-2 py-1.5 text-2xs font-medium uppercase tracking-wide text-text-subtle", className)}
      {...props}
    />
  );
}

export function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return <SelectPrimitive.Separator className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />;
}
